"use client";

import { Box, Flex, Skeleton } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useThemeTokens } from "@/lib/theme/tokens";

export function ModalSectionHeading({
  icon,
  title,
}: {
  icon: ReactNode;
  title: string;
}) {
  const theme = useThemeTokens();

  return (
    <Flex align="center" gap={2} mb={3}>
      <Box color={theme.accent.primary} flexShrink={0}>
        {icon}
      </Box>
      <Box
        as="span"
        fontSize="sm"
        fontWeight="700"
        color={theme.text.primary}
        textTransform="uppercase"
        letterSpacing="0.05em"
      >
        {title}
      </Box>
    </Flex>
  );
}

export function ModalDetailRow({
  label,
  value,
  isLast = false,
}: {
  label: string;
  value: ReactNode;
  isLast?: boolean;
}) {
  const theme = useThemeTokens();

  return (
    <Flex
      align={{ base: "flex-start", md: "center" }}
      direction={{ base: "column", md: "row" }}
      gap={{ base: 1, md: 3 }}
      py={{ base: 2, md: 2.5 }}
      borderBottomWidth={isLast ? "0" : "1px"}
      borderColor={theme.borderGlass.subtle}
    >
      <Box
        flex={{ md: "0 0 130px" }}
        fontSize="xs"
        color={theme.text.tertiary}
        fontWeight="400"
        lineHeight="1.4"
      >
        {label}
      </Box>
      <Box flex={1} minW={0} w={{ base: "100%", md: "auto" }}>
        <Box
          fontSize="sm"
          color={theme.text.primary}
          fontWeight="500"
          lineHeight="1.5"
          wordBreak="break-word"
        >
          {value}
        </Box>
      </Box>
    </Flex>
  );
}

/** Long-form text: label above, content in a full-width block (not side-by-side). */
export function ModalDescriptionBlock({
  label = "Description",
  text,
  isLast = false,
  emptyText = "-",
}: {
  label?: string;
  text?: string | null;
  isLast?: boolean;
  emptyText?: string;
}) {
  const theme = useThemeTokens();
  const trimmed = text?.trim();

  return (
    <Box
      py={2.5}
      borderBottomWidth={isLast ? "0" : "1px"}
      borderColor={theme.borderGlass.subtle}
    >
      <Box
        fontSize="xs"
        color={theme.text.tertiary}
        fontWeight="400"
        mb={2}
      >
        {label}
      </Box>
      {trimmed ? (
        <Box
          py={3}
        >
          <Box
            fontSize="sm"
            color={theme.text.primary}
            fontWeight="400"
            lineHeight="1.7"
            whiteSpace="pre-line"
            wordBreak="break-word"
          >
            {trimmed}
          </Box>
        </Box>
      ) : (
        <Box fontSize="sm" color={theme.text.secondary} fontStyle="italic">
          {emptyText}
        </Box>
      )}
    </Box>
  );
}

export function ModalPreviewSkeleton({ rowCount = 6 }: { rowCount?: number }) {
  return (
    <Flex direction="column" gap={5}>
      <Skeleton height="24px" width="96px" borderRadius="full" />
      <Skeleton height="180px" width="100%" borderRadius="12px" />
      {Array.from({ length: rowCount }).map((_, index) => (
        <Flex key={index} gap={4} py={2}>
          <Skeleton height="12px" width="140px" borderRadius="4px" />
          <Skeleton height="14px" flex={1} borderRadius="4px" />
        </Flex>
      ))}
    </Flex>
  );
}

export function ModalDetailCard({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  const theme = useThemeTokens();

  return (
    <Box
      borderRadius="xl"
      borderWidth="1px"
      borderColor={theme.borderGlass.default}
      overflow="hidden"
    >
      <Box px={{ base: 4, md: 5 }} pt={4}>
        <ModalSectionHeading icon={icon} title={title} />
      </Box>
      <Box px={{ base: 4, md: 5 }} pb={2}>
        {children}
      </Box>
    </Box>
  );
}
